import { model as i18n } from '!/i18n'
import { hooks, type RouterResponse } from '!/router'
import { register } from '!/repository'
import { combine, createEvent, createStore, sample } from 'effector'
import { persist } from 'effector-storage/session'
import { api } from '@setplex/tria-api'
import { profilesModel } from '~/entities/profiles'
import { model as session } from '~/entities/session'
import { $enabled } from './feature'
import { noop } from '~/shared/effector'

/**
 * Contains correct subscriber's PIN code value
 */
export const $subscriberPin = api.session.$session //
  .map((session) => session?.subscriberPin ?? null)

//
// Maintain unlocked content ID
// This logic is based on the assumption that all content IDs are unique, even for the different content types,
// and also that ID is a number (which will not be always true in the future, but for now it is true).
// At least second assumption will be false in the future, so we will need to refactor this logic then.
//

/**
 * Unlock content by ID
 */
export const unlockId = createEvent<number | null | undefined>()

/**
 * Reset the unlocked content ID to `null`.
 */
export const resetUnlockId = createEvent()

/**
 * Store that contains the ID of the last unlocked content.
 * It is used to determine whether the content is locked or not currently.
 */
export const $unlockedId = createStore<number | null>(null)
  .on(unlockId, (_, id) => id ?? null)
  .reset(resetUnlockId)

// Persist the unlocked content ID in the browser's session storage.
// This is necessary to keep the unlocked content ID across page reloads.
//
// TODO: Currently there are some questions about PIN lock status across different tabs, might refactor this in the future
// https://bldsoft.slack.com/archives/C02LLFVSJQ0/p1753283517861989
//
// we do not use scopes for now, so, ignore this rule
// eslint-disable-next-line effector/require-pickup-in-persist
persist({
  store: $unlockedId,
  key: 'tria__unlocked-content-id',
  fail: noop,
  serialize: (id?: number | null) =>
    id == null || isNaN(id) ? '-' : String(id),
  deserialize: (id?: string | null) =>
    id == null || id === '-' ? null : Number(id) || null, // this will make '0' as null, if this is not desired, refactor this
})

//
// Reset the unlocked content ID on any app page change
//

const navigationHook = createEvent<RouterResponse | null | undefined>()

const navigationInitHook = navigationHook //
  .prepend<{ route: RouterResponse }>(({ route }) => route)

const navigationLeftHook = navigationHook //
  .prepend<{ to: RouterResponse }>(({ to }) => to)

// register the navigation hooks to reset the unlocked content ID
hooks.register(hooks.Init, navigationInitHook)
hooks.register(hooks.Left, navigationLeftHook)

/**
 * Store that contains the previous and current routes,
 * to maintain the correct unlocked content ID on the same route navigation.
 * This is essentially routes change history, so we can check if the route has changed and to what.
 */
type RouteRef = { ref: unknown; id?: number } | null
const $routes = createStore<[RouteRef, RouteRef]>([null, null])

/**
 * Helper function to get the content ID from the route, using the `meta.contentId` function.
 * Route should define it in the `meta` object, in order to preserve unlocked content ID during navigation.
 */
function getMetaId(route?: RouterResponse | null) {
  return route == null ||
    route.__resolved == null ||
    typeof route.__resolved !== 'object' ||
    !('meta' in route.__resolved) ||
    route.__resolved.meta == null ||
    typeof route.__resolved.meta !== 'object' ||
    !('contentId' in route.__resolved.meta) ||
    typeof route.__resolved.meta.contentId !== 'function' // ensure that the route has the meta with `contentId` function
    ? null
    : route.__resolved.meta.contentId({ ...route.props }) || null // this will make '0' as null, if this is not desired, refactor this
}

// handle navigation hooks
// updates the current and previous routes in the `$routes` store in two cases:
// 1. when the route changes (i.e. the current route is different from the previous one)
// 2. when the continuous content ID changes (i.e. the `meta.contentId` function returns a different ID)
sample({
  clock: navigationHook,
  source: $routes,
  filter: ([prev, curr], route) => {
    if (prev == null || curr == null) return true // initial route. NB! this will reset unlockedId on Ctrl+R or F5
    if (route?.__resolved !== curr.ref) return true // route has changed
    if (getMetaId(route) !== curr.id) return true // same route, but continuous content ID has changed
    return false
  },
  fn: ([, curr], route) => {
    return [
      curr ?? null, // current route becomes the previous route
      route ? { ref: route.__resolved, id: getMetaId(route) } : null, // new current route
    ] as const
  },
  target: $routes,
})

// reset the unlocked content ID on any route change,
// but it will preserve unlocked ID in case route has the same content ID as unlocked one,
// this allows to keep the unlocked content when navigating to the content details page
sample({
  clock: $routes,
  source: $unlockedId,
  filter: (unlockedId, [prev, curr]) => {
    if (prev === null || curr === null) return true // initial route. NB! this will reset unlockedId on Ctrl+R or F5
    if (curr.id === unlockedId) return false // same continuous content ID, do not reset unlockedId!
    return true
  },
  fn: () => undefined,
  target: resetUnlockId,
})

//
// Reset the unlocked content ID on any session change
//

sample({
  clock: [session.$accountNumber, profilesModel.$activeProfileId],
  fn: () => undefined,
  target: resetUnlockId,
})

//
// Pipes, to use in models and skins
//

/**
 * Store that contains function to check if the content is locked at the moment.
 * It takes into account the content's lockable state, the current unlocked content ID and the feature enabled state.
 * If the pin lock feature is disabled, it always returns `false`.
 */
export const $isLockedFn = combine(
  $enabled,
  $unlockedId,
  (enabled, unlockedId) => {
    return (content?: { id?: number; locked?: boolean } | null) => {
      if (!enabled || content == null) return false
      return Boolean(content.locked) && content.id !== unlockedId
    }
  }
)

const $lockedBannerSubtitle = combine(
  i18n.$t,
  $isLockedFn,
  ({ t }, isLockedFn) =>
    (content: { locked?: boolean; description?: string }) =>
      isLockedFn(content) ? t('locked_popup_hint') : content.description
)

/**
 * Store that contains function to check if the content is lockable.
 * It takes into account the feature enabled state.
 * If the pin lock feature is disabled, it always returns `false`.
 */
export const $isLockableFn = combine($enabled, (enabled) => {
  return (content?: { id?: number; locked?: boolean } | null) => {
    if (!enabled || content == null) return false
    return Boolean(content.locked)
  }
})

register($isLockedFn, '|features/pin-lock/isLocked')
register($lockedBannerSubtitle, '|features/pin-lock/lockedBannerSubtitle')
register(resetUnlockId, 'features/pin-lock/resetUnlockId')
